"use client";
import { toDayHrMinSec } from "@/utils";


import { useCallback, useEffect, useState } from "react";
import classes from "./OfferTimeout.module.css";

interface Props {
  startTime?: number;
  endDate: string;
  timeEnded?: () => void;
  type?: "sm" | "lg";
  className?: string;
  title?: string;
}

const OfferTimeout = ({
  startTime,
  endDate,
  timeEnded = () => {},
  type,
  className,
  title,
}: Props) => {
  const [timeDistance, setTimeDistance] = useState(0);

  useEffect(() => {
    const dis = Math.max(new Date(endDate).getTime() - new Date().getTime(), 0);
    setTimeDistance(dis);

    if (dis <= 0) timeEnded();

    const timer = setInterval(() => {
      setTimeDistance((prevTime) => {
        const newTime = prevTime - 1000;

        if (newTime <= 0) timeEnded();

        return Math.max(newTime, 0);
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [endDate, timeEnded]);

  const timerArr = toDayHrMinSec(timeDistance);

  return (
    <div className={[classes.OfferTimeout, className].join(" ")}>
      <CountDownTimer targetDate={endDate} title={title} />
    </div>
  );
};

export default OfferTimeout;

interface TimeLeft {
  days: string;
  hours: string;
  minutes: string;
  seconds: string;
}

interface CountDownTimerProps {
  targetDate: string; // Format: "YYYY-MM-DD"
  title?: string;
}

const CountDownTimer: React.FC<CountDownTimerProps> = ({
  targetDate,
  title,
}) => {
  const [countDownTime, setCountDownTime] = useState<TimeLeft>({
    days: "00",
    hours: "00",
    minutes: "00",
    seconds: "00",
  });

  const getTimeDifference = (countDownTimeMs: number): void => {
    const currentTime = new Date().getTime();
    const timeDifference = countDownTimeMs - currentTime;

    let days: string =
      Math.floor(timeDifference / (24 * 60 * 60 * 1000)) >= 10
        ? Math.floor(timeDifference / (24 * 60 * 60 * 1000)).toString()
        : `0${Math.floor(timeDifference / (24 * 60 * 60 * 1000))}`;

    const hours: string =
      Math.floor((timeDifference % (24 * 60 * 60 * 1000)) / (1000 * 60 * 60)) >=
      10
        ? Math.floor(
            (timeDifference % (24 * 60 * 60 * 1000)) / (1000 * 60 * 60)
          ).toString()
        : `0${Math.floor(
            (timeDifference % (24 * 60 * 60 * 1000)) / (1000 * 60 * 60)
          )}`;

    const minutes: string =
      Math.floor((timeDifference % (60 * 60 * 1000)) / (1000 * 60)) >= 10
        ? Math.floor(
            (timeDifference % (60 * 60 * 1000)) / (1000 * 60)
          ).toString()
        : `0${Math.floor((timeDifference % (60 * 60 * 1000)) / (1000 * 60))}`;

    const seconds: string =
      Math.floor((timeDifference % (60 * 1000)) / 1000) >= 10
        ? Math.floor((timeDifference % (60 * 1000)) / 1000).toString()
        : `0${Math.floor((timeDifference % (60 * 1000)) / 1000)}`;

    if (timeDifference < 0) {
      setCountDownTime({
        days: "00",
        hours: "00",
        minutes: "00",
        seconds: "00",
      });
    } else {
      setCountDownTime({
        days,
        hours,
        minutes,
        seconds,
      });
    }
  };

  const startCountDown = useCallback(() => {
    const countDownDate = new Date(targetDate).getTime();
    const interval = setInterval(() => {
      getTimeDifference(countDownDate);
    }, 1000);

    // Cleanup interval on component unmount
    return () => clearInterval(interval);
  }, [targetDate]);

  useEffect(() => {
    const cleanup = startCountDown();
    return cleanup;
  }, [startCountDown]);

  return (
    <div className="">
      <div className="flex flex-col items-center justify-center w-full h-full gap-4 sm:gap-8">
        <h2 className="text-2xl sm:text-3xl font-semibold text-center tracking-widest px-2">
          {title || "Offer Ends In:"}
        </h2>

        <div className="flex justify-center gap-3 sm:gap-8">
          <div className="flex flex-col gap-5 relative">
            <div className="h-16 w-16 sm:w-32 sm:h-32 lg:w-40 lg:h-40 flex justify-between items-center bg-primary/10 rounded-lg">
              <div className="relative h-2.5 w-2.5 sm:h-3 sm:w-3 !-left-[6px] rounded-full bg-white"></div>
              <span className="lg:text-7xl sm:text-6xl text-3xl font-semibold text-primary">
                {countDownTime.days}
              </span>
              <div className="relative h-2.5 w-2.5 sm:h-3 sm:w-3 -right-[6px] rounded-full bg-white"></div>
            </div>
            <span className="text-primary text-xs sm:text-2xl text-center capitalize">
              {countDownTime.days === "01" ? "Day" : "Days"}
            </span>
          </div>
          <div className="flex flex-col gap-5 relative">
            <div className="h-16 w-16 sm:w-32 sm:h-32 lg:w-40 lg:h-40 flex justify-between items-center bg-primary/10 rounded-lg">
              <div className="relative h-2.5 w-2.5 sm:h-3 sm:w-3 !-left-[6px] rounded-full bg-white"></div>
              <span className="lg:text-7xl sm:text-6xl text-3xl font-semibold text-primary">
                {countDownTime.hours}
              </span>
              <div className="relative h-2.5 w-2.5 sm:h-3 sm:w-3 -right-[6px] rounded-full bg-white"></div>
            </div>
            <span className="text-primary text-xs sm:text-2xl text-center font-medium">
              {countDownTime.hours === "01" ? "Hour" : "Hours"}
            </span>
          </div>
          <div className="flex flex-col gap-5 relative">
            <div className="h-16 w-16 sm:w-32 sm:h-32 lg:w-40 lg:h-40 flex justify-between items-center bg-primary/10 rounded-lg">
              <div className="relative h-2.5 w-2.5 sm:h-3 sm:w-3 !-left-[6px] rounded-full bg-white"></div>
              <span className="lg:text-7xl sm:text-6xl text-3xl font-semibold text-primary">
                {countDownTime.minutes}
              </span>
              <div className="relative h-2.5 w-2.5 sm:h-3 sm:w-3 -right-[6px] rounded-full bg-white"></div>
            </div>
            <span className="text-primary text-xs sm:text-2xl text-center capitalize">
              {countDownTime.minutes === "01" ? "Minute" : "Minutes"}
            </span>
          </div>
          <div className="flex flex-col gap-5 relative">
            <div className="h-16 w-16 sm:w-32 sm:h-32 lg:w-40 lg:h-40 flex justify-between items-center bg-primary/10 rounded-lg">
              <div className="relative h-2.5 w-2.5 sm:h-3 sm:w-3 !-left-[6px] rounded-full bg-white"></div>
              <span className="lg:text-7xl sm:text-6xl text-3xl font-semibold text-primary">
                {countDownTime.seconds}
              </span>
              <div className="relative h-2.5 w-2.5 sm:h-3 sm:w-3 -right-[6px] rounded-full bg-white"></div>
            </div>
            <span className="text-primary text-xs sm:text-2xl text-center capitalize">
              {countDownTime.seconds === "01" ? "Second" : "Seconds"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
